import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { fetchUnreadPortalNotificationCount } from '@/lib/queries';
import type { PortalNotification } from '@/types/portal';

interface PortalRealtimeContextType {
  unreadNotifications: number;
  refreshUnread: () => void;
}

const PortalRealtimeContext = createContext<PortalRealtimeContextType>({
  unreadNotifications: 0,
  refreshUnread: () => {},
});

/** Fired on window when a new portal notification arrives (RLS-scoped to recipient) */
export const PORTAL_NOTIFICATION_EVENT = 'portal-notification';

export function PortalRealtimeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const refreshUnread = useCallback(() => {
    if (!user) return;
    fetchUnreadPortalNotificationCount()
      .then(setUnreadNotifications)
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    refreshUnread();
  }, [refreshUnread]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`portal-user-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        payload => {
          const notification = payload.new as PortalNotification;
          if (notification.app !== 'portal') return;
          toast(notification.title, {
            description: notification.body ?? undefined,
          });
          setUnreadNotifications(count => count + 1);
          window.dispatchEvent(
            new CustomEvent(PORTAL_NOTIFICATION_EVENT, { detail: notification })
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PortalRealtimeContext.Provider value={{ unreadNotifications, refreshUnread }}>
      {children}
    </PortalRealtimeContext.Provider>
  );
}

export function usePortalRealtime() {
  return useContext(PortalRealtimeContext);
}
